import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getActivities, createActivity, updateActivity, deleteActivity, archiveActivity,
  getSessionsForActivity, createSession, updateSession, deleteSession,
  provisionUser,
} from '../../util/api';
import { logoutThunk } from '../../ducks/auth.duck';

// ─── Async Thunks ───

const fetchActivitiesThunk = createAsyncThunk(
  'VendorDashboardPage/fetchActivities',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const r = await getActivities();
      return r.data || [];
    } catch (e: any) {
      // 401 = Supabase session expired/invalid → log out both Supabase and Sharetribe
      if (e.status === 401) {
        dispatch(logoutThunk());
        return rejectWithValue('Session expired. Please log in again.');
      }
      // 403 = no RBAC group yet → auto-provision and retry
      if (e.status === 403) {
        try {
          await provisionUser();
          const retry = await getActivities();
          return retry.data || [];
        } catch (retryErr: any) {
          return rejectWithValue(retryErr.message || 'Failed after RBAC provisioning');
        }
      }
      return rejectWithValue(e.message || 'Failed to load activities');
    }
  }
);

const fetchSessionsThunk = createAsyncThunk(
  'VendorDashboardPage/fetchSessions',
  async (activityId: string, { rejectWithValue }) => {
    try {
      const r = await getSessionsForActivity(activityId);
      return { activityId, sessions: r.data || [] };
    } catch (e: any) { return rejectWithValue(e.message || 'Failed to load sessions'); }
  }
);

// ─── Slice ───

const slice = createSlice({
  name: 'VendorDashboardPage',
  initialState: {
    activities: [] as any[],
    activitiesLoading: false,
    activitiesError: null as string | null,
    sessions: {} as Record<string, any[]>,
    sessionsLoading: false,
    sessionsError: null as string | null,
    mutationInProgress: false,
    mutationError: null as string | null,
  },
  reducers: {
    mutationStart: s => { s.mutationInProgress = true; s.mutationError = null; },
    mutationEnd: s => { s.mutationInProgress = false; },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchActivitiesThunk.pending, s => { s.activitiesLoading = true; s.activitiesError = null; })
      .addCase(fetchActivitiesThunk.fulfilled, (s, a) => { s.activitiesLoading = false; s.activities = a.payload; })
      .addCase(fetchActivitiesThunk.rejected, (s, a) => { s.activitiesLoading = false; s.activitiesError = a.payload as string; })

      .addCase(fetchSessionsThunk.pending, s => { s.sessionsLoading = true; s.sessionsError = null; })
      .addCase(fetchSessionsThunk.fulfilled, (s, a) => { s.sessionsLoading = false; s.sessions[a.payload.activityId] = a.payload.sessions; })
      .addCase(fetchSessionsThunk.rejected, (s, a) => { s.sessionsLoading = false; s.sessionsError = a.payload as string; });
  },
});

export default slice.reducer;

// ─── Exported Thunks ───

export const fetchVendorActivities = () => (dispatch: any) =>
  dispatch(fetchActivitiesThunk()).unwrap();

export const fetchSessionsForActivity = (activityId: string) => (dispatch: any) =>
  dispatch(fetchSessionsThunk(activityId)).unwrap();

const { mutationStart, mutationEnd } = slice.actions;

const mutate = (fn: () => Promise<any>, refreshes: any[]) => async (dispatch: any) => {
  dispatch(mutationStart());
  try {
    await fn();
    for (const r of refreshes) dispatch(r);
  } finally {
    dispatch(mutationEnd());
  }
};

export const createNewActivity = (body: any) =>
  mutate(() => createActivity(body), [fetchActivitiesThunk()]);

export const updateExistingActivity = (body: any) =>
  mutate(() => updateActivity(body), [fetchActivitiesThunk()]);

export const setExistingActivityStatus = (id: string, status: string) =>
  mutate(() => updateActivity({ id, status }), [fetchActivitiesThunk()]);

export const publishActivity = (id: string) => setExistingActivityStatus(id, 'published');
export const closeActivity = (id: string) => setExistingActivityStatus(id, 'closed');
export const openActivity = (id: string) => setExistingActivityStatus(id, 'published');

// Hard-delete: only for drafts. Discards Sharetribe draft + deletes Supabase row.
export const deleteExistingActivity = (id: string) =>
  mutate(() => deleteActivity({ id }), [fetchActivitiesThunk()]);

// Archive: for non-draft activities. Closes listing + soft-flags in Supabase.
export const archiveExistingActivity = (id: string) =>
  mutate(() => archiveActivity({ id }), [fetchActivitiesThunk()]);

export const createNewSession = (body: any, activityId: string) =>
  mutate(() => createSession(body), [fetchSessionsThunk(activityId), fetchActivitiesThunk()]);

export const updateExistingSession = (body: any, activityId: string) =>
  mutate(() => updateSession(body), [fetchSessionsThunk(activityId)]);

export const deleteExistingSession = (id: string, activityId: string) =>
  mutate(() => deleteSession({ id }), [fetchSessionsThunk(activityId), fetchActivitiesThunk()]);

// ─── loadData ───

export const loadData = () => (dispatch: any) => dispatch(fetchActivitiesThunk());

// ─── Selectors ───

export const selectActivities = (s: any) => s.VendorDashboardPage?.activities || [];
export const selectActivitiesLoading = (s: any) => s.VendorDashboardPage?.activitiesLoading || false;
export const selectSessions = (s: any, activityId: string) => s.VendorDashboardPage?.sessions?.[activityId] || [];
export const selectSessionsLoading = (s: any) => s.VendorDashboardPage?.sessionsLoading || false;
export const selectMutationInProgress = (s: any) => s.VendorDashboardPage?.mutationInProgress || false;
